import React, { PropsWithChildren } from 'react';
import styled from 'styled-components';
import { HelpIcon, HelpIconContainer } from '../helpIconContainer';
import { Position } from '../ztooltip';

export type RadioVariant = 'default' | 'ai';

const StyledDefault = styled.div<{ $fullWidth?: boolean; disabled?: boolean; $variant?: RadioVariant }>`
    font-family: ${(props) => props.theme.app.font};
    text-align: left;
    border: none;
    display: ${(props) => (props.$fullWidth ? 'flex' : 'inline-flex')};
    width: ${(props) => (props.$fullWidth ? '100%' : 'auto')};
    transition: background-color ease 0.2s;
    cursor: pointer;
    position: relative;
    margin: 0;
    padding: 4px 0;
    align-items: flex-start;
    min-height: 24px;
    flex-direction: column;

    label {
        font-size: ${(props) => props.theme.app.component.radio.textSize};
        font-weight: ${(props) => props.theme.app.component.radio.textWeight};
        margin-right: 10px;
        padding-left: 24px !important;
    }

    input[type='radio']:checked,
    input[type='radio']:not(:checked) {
        display: none;
    }

    input[type='radio']:checked + label,
    input[type='radio']:not(:checked) + label {
        position: relative;
        padding-left: 24px;
        cursor: pointer;
        line-height: 16px;
        display: inline-flex;
        font-size: ${(props) => props.theme.app.component.radio.textSize};
        font-weight: ${(props) => props.theme.app.component.radio.textWeight};
    }

    input[type='radio']:checked + label:before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        width: ${(props) => props.theme.app.component.radio.width};
        height: ${(props) => props.theme.app.component.radio.height};
        border: ${(props) =>
            props.$variant === 'ai'
                ? '2px solid #3B82F6'
                : props.disabled
                  ? props.theme.app.component.radio.selected.disabled.border
                  : props.theme.app.component.radio.selected.active.border};
        border-radius: ${(props) => props.theme.app.component.radio.borderRadius};
        background: ${(props) => props.theme.app.component.radio.selected.active.background};
    }

    input[type='radio']:not(:checked) + label:before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        width: ${(props) => props.theme.app.component.radio.width};
        height: ${(props) => props.theme.app.component.radio.height};
        border: ${(props) =>
            props.disabled ? props.theme.app.component.radio.unselected.disabled.border : props.theme.app.component.radio.unselected.active.border};
        border-radius: ${(props) => props.theme.app.component.radio.borderRadius};
        background: ${(props) =>
            props.disabled ? props.theme.app.component.radio.unselected.disabled.background : props.theme.app.component.radio.unselected.active.background};
    }

    input[type='radio']:checked + label:after,
    input[type='radio']:not(:checked) + label:after {
        content: '';
        width: 8px;
        height: 8px;
        background: ${(props) =>
            props.$variant === 'ai'
                ? 'linear-gradient(90deg, #3B82F6 3.75%, #8B5CF6 103.75%)'
                : props.disabled
                  ? props.theme.app.component.radio.selected.disabled.inputColor
                  : props.theme.app.component.radio.selected.active.inputColor};
        position: absolute;
        left: 5px;
        top: 5px;
        border-radius: 50%;
        -webkit-transition: all 0.2s ease;
        transition: all 0.2s ease;
        border: none;
    }

    input[type='radio']:not(:checked) + label:after {
        opacity: 0;
        transform: scale(0);
    }

    input[type='radio']:checked + label:after {
        opacity: 1;
        transform: scale(1);
    }
`;

const RadioLabelContainer = styled.div`
    padding-top: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    font-size: ${(props) => props.theme.app.component.radio.textSize} !important;
    font-weight: ${(props) => props.theme.app.component.radio.textWeight} !important;
    margin-left: 5px;
`;

const RadioCaption = styled.span`
    display: inline-block;
    font-size: 12px;
    line-height: 16px;
    color: rgb(68, 90, 100);
`;

const CaptionContainer = styled.div`
    display: flex;
    margin-left: 34px;
`;

const ParentContainer = styled.div`
    display: inline-flex;
    flex-direction: row;
    align-items: flex-start;
`;

const BadgeContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;

    > *:first-child {
        margin-left: 24px;
    }
`;

const StyledHelpIconContainer = styled(HelpIconContainer)`
    margin-left: 5px;
`;

interface RadioProps<T extends number | string> {
    id?: string;
    name: string;
    label: string | React.ReactNode;
    caption?: React.ReactNode;
    value: T;
    onSelect?: (value: T) => void;
    color?: string;
    fontSize?: string;
    fontFamily?: string;
    checked?: boolean;
    className?: string;
    disabled?: boolean;
    tooltip?: string;
    tooltipPosition?: Position;
    variant?: RadioVariant;
    badges?: React.ReactNode[];
}

export const Radio = <T extends number | string>(props: PropsWithChildren<RadioProps<T>>) => {
    const handleChange = () => {
        if (props.onSelect) props.onSelect(props.value);
    };

    return (
        <>
            <StyledDefault className={props.className} disabled={props.disabled} $variant={props.variant}>
                <ParentContainer>
                    <input
                        id={props.id}
                        disabled={props.disabled}
                        type='radio'
                        name={props.name}
                        value={props.value}
                        checked={props.checked}
                        onChange={handleChange}
                    />
                    <label htmlFor={props.id} onClick={() => !props.disabled && handleChange()}>
                        <RadioLabelContainer>{props.label}</RadioLabelContainer>
                    </label>
                    {props.tooltip && (
                        <StyledHelpIconContainer tooltip={props.tooltip} position={props.tooltipPosition}>
                            <HelpIcon />
                        </StyledHelpIconContainer>
                    )}

                    {props.badges && props.badges.length > 0 && (
                        <BadgeContainer>
                            {props.badges.map((badge, index) => (
                                <React.Fragment key={index}>{badge}</React.Fragment>
                            ))}
                        </BadgeContainer>
                    )}
                </ParentContainer>

                {props.caption && (
                    <CaptionContainer>
                        <RadioCaption>{props.caption}</RadioCaption>
                    </CaptionContainer>
                )}
            </StyledDefault>
        </>
    );
};
